import { useState, useEffect, useRef, useMemo } from 'react';
import { apiGet, apiPost, apiDelete, apiPut } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { VisaCenterLoading } from '@/components/LoadingStates';
import Pagination from '@/components/Pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, 
  Scan, 
  Plus, 
  Search,
  AlertTriangle,
  CheckCircle,
  Clock,
  Trash2,
  Eye,
  User,
  Calendar,
  MapPin,
  Loader2,
  Save,
  FileText,
  Paperclip,
  Link,
  Unlink,
  RefreshCw,
  Upload,
  Image as ImageIcon,
  CreditCard,
  Globe,
  Camera
} from 'lucide-react';
import WorkerDocuments from '@/components/WorkerDocuments';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

// Animation variants for new design
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Helper function to format base64 image with proper data URI prefix
const formatBase64Image = (base64String) => {
  if (!base64String) return null;
  // If already has data URI prefix, return as is
  if (base64String.startsWith('data:image')) return base64String;
  // Add prefix for raw base64 strings
  return `data:image/jpeg;base64,${base64String}`;
};

// Parse date in DD/MM/YYYY format
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  // Handle YYYY-MM-DD format
  if (dateStr.includes('-') && dateStr.length === 10) {
    const [year, month, day] = dateStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  
  // Handle DD/MM/YYYY format
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  }
  
  return new Date(dateStr);
};

// Calculate days remaining - THIS IS THE SINGLE SOURCE OF TRUTH
// Uses entry_date + duration_days if available, otherwise falls back to expiry_date
const calculateDaysRemaining = (expiryDate, entryDate = null, durationDays = null, issueDate = null) => {
  // Priority: entry_date > issue_date > expiry_date
  const startDate = entryDate || issueDate;
  
  // If we have a start date and duration_days, calculate remaining days from them
  if (startDate && durationDays) {
    const start = parseDate(startDate);
    if (start && !isNaN(start.getTime())) {
      const calculatedExpiry = new Date(start);
      calculatedExpiry.setDate(calculatedExpiry.getDate() + parseInt(durationDays));
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      calculatedExpiry.setHours(0, 0, 0, 0);
      
      const diffTime = calculatedExpiry - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  }
  
  // Fallback to expiry_date
  if (!expiryDate) return null;
  
  const expiry = parseDate(expiryDate);
  if (!expiry || isNaN(expiry.getTime())) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  
  const diffTime = expiry - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Get status config based on days remaining
// remaining_days > 10        → ACTIVE (Green)
// remaining_days 6–10        → EXPIRING SOON (Orange)
// remaining_days <= 5        → EXPIRED/URGENT (Red)
const getStatusConfig = (daysRemaining, isChangeStatus = false) => {
  // If CHANGE_STATUS, show blue styling
  if (isChangeStatus) {
    return {
      status: 'change_status',
      headerBg: 'bg-blue-500/15',
      headerBorder: 'border-blue-500/40',
      textColor: 'text-blue-400',
      icon: '🔄',
      text: 'STATUS CHANGED',
      cardBg: 'bg-gradient-to-br from-[#0F172A] to-[#1E293B]',
      cardBorder: 'border-2 border-blue-500/40',
      alertBanner: null,
      daysColor: '#3B82F6',
      showStamp: true
    };
  }
  
  if (daysRemaining === null) {
    return {
      status: 'active',
      headerBg: 'bg-emerald-500/15',
      headerBorder: 'border-emerald-500/40',
      textColor: 'text-emerald-400',
      icon: '✓',
      text: 'ACTIVE',
      cardBg: 'bg-gradient-to-br from-[#0F172A] to-[#1E293B]',
      cardBorder: 'border-none',
      alertBanner: null,
      daysColor: '#10B981',
      showStamp: false
    };
  }
  
  // ACTIVE: > 10 days
  if (daysRemaining > 10) {
    return {
      status: 'active',
      headerBg: 'bg-emerald-500/15',
      headerBorder: 'border-emerald-500/40',
      textColor: 'text-emerald-400',
      icon: '✓',
      text: 'ACTIVE',
      cardBg: 'bg-gradient-to-br from-[#0F172A] to-[#1E293B]',
      cardBorder: 'border-none',
      alertBanner: null,
      daysColor: '#10B981',
      showStamp: false
    };
  }
  
  // EXPIRING SOON: 6-10 days
  if (daysRemaining > 5) {
    return {
      status: 'expiring_soon',
      headerBg: 'bg-amber-500/25',
      headerBorder: 'border-amber-500/60',
      textColor: 'text-amber-400',
      icon: '⚠️',
      text: 'EXPIRING SOON',
      cardBg: 'bg-gradient-to-br from-[#1A1408] to-[#2D2310]',
      cardBorder: 'border-2 border-amber-500/40',
      alertBanner: {
        bg: 'bg-gradient-to-r from-amber-600 via-amber-700 to-amber-600',
        text: 'الفـيـزا قـريـبة مـن الانـتـهاء - VISA EXPIRING SOON',
        icon: '⏰'
      },
      daysColor: '#F59E0B',
      showStamp: false
    };
  }
  
  // EXPIRED/URGENT: <= 5 days
  return {
    status: 'expired',
    headerBg: 'bg-gradient-to-r from-red-800 to-red-700',
    headerBorder: 'border-red-500',
    textColor: 'text-white',
    icon: '🚨',
    text: daysRemaining < 0 ? 'EXPIRED' : 'URGENT',
    cardBg: 'bg-gradient-to-br from-[#1F0A0A] to-[#2D1010]',
    cardBorder: 'border-2 border-red-500',
    alertBanner: {
      bg: 'bg-gradient-to-r from-red-600 via-red-700 to-red-600',
      text: daysRemaining < 0 
        ? '⚠️ إجراء عاجل مطلوب - URGENT ACTION REQUIRED ⚠️'
        : `⚠️ VISA EXPIRING IN ${daysRemaining} DAYS - URGENT ⚠️`,
      icon: '⚠️'
    },
    daysColor: '#DC3545',
    showStamp: false
  };
};

// Get worker status icon
const getStatusIcon = (status) => {
  const icons = {
    'abroad': '✈️',
    'arrived': '✅',
    'runaway': '🚨',
    'noarrival': '❌',
    'settled': '💰'
  };
  return icons[status] || '📋';
};

// Visa Card Component - IDENTICAL to VisaTrackerPage design
const VisaCard = ({ visa, onView, onDelete, onToggleProcess, onUnlink, onLink, onSetPassportExpiry, onViewDocs }) => {
  const daysRemaining = calculateDaysRemaining(visa.expiry_date, visa.entry_date, visa.duration_days, visa.issue_date);
  const hasActionTaken = visa.actionTaken || visa.action_taken;
  const hasStatusChanged = visa.linkedWorkerProcessStatus === 'CHANGE_STATUS';
  const showStamp = hasActionTaken || hasStatusChanged;
  
  // Get status config matching VisaTrackerPage
  const getVisaStatus = () => {
    if (showStamp) {
      return {
        status: 'action_taken',
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-500/15',
        borderColor: 'border-emerald-500',
        cardBg: 'bg-gradient-to-br from-emerald-900/30 to-emerald-950/50',
        text: 'ACTION TAKEN'
      };
    }
    
    if (daysRemaining === null || daysRemaining > 10) {
      return {
        status: 'active',
        color: 'text-emerald-400',
        bgColor: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/30',
        cardBg: 'bg-gradient-to-br from-[#1E293B] to-[#0F172A]',
        text: 'ACTIVE'
      };
    }
    
    if (daysRemaining > 5) {
      return {
        status: 'expiring_soon',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10',
        borderColor: 'border-amber-500/50',
        cardBg: 'bg-gradient-to-br from-amber-900/20 to-[#0F172A]',
        text: 'EXPIRING SOON'
      };
    }
    
    return {
      status: 'expired',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/50',
      cardBg: 'bg-gradient-to-br from-red-900/20 to-[#0F172A]',
      text: daysRemaining < 0 ? 'EXPIRED' : 'URGENT'
    };
  };
  
  const statusConfig = getVisaStatus();
  
  const daysText = daysRemaining === null || isNaN(daysRemaining)
    ? 'N/A'
    : Math.abs(daysRemaining);
    
  const daysLabel = daysRemaining === null || isNaN(daysRemaining)
    ? ''
    : daysRemaining >= 0 
      ? 'days remaining' 
      : 'days expired';

  // Get initials for photo placeholder
  const initials = (visa.passenger_name || 'UN').split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase();

  return (
    <Card 
      className={`relative rounded-2xl overflow-visible border-2 ${statusConfig.borderColor} ${statusConfig.cardBg}`}
      data-testid={`visa-card-${visa.id}`}
    >
      {/* Status Header */}
      <div className={`${statusConfig.bgColor} border-b ${statusConfig.borderColor} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <CheckCircle className={`h-4 w-4 ${statusConfig.color}`} />
          <span className={`${statusConfig.color} text-xs font-bold`}>
            {showStamp ? 'STATUS CHANGED' : statusConfig.text}
          </span>
        </div>
        <span className="text-white/50 text-xs font-mono">
          #{(visa.visa_number || visa.id || '').substring(0, 12).toUpperCase()}
        </span>
      </div>

      {/* Alert Banner */}
      {showStamp && (
        <div className="bg-emerald-600 text-center py-2">
          <span className="text-white text-xs font-bold">✓ STATUS CHANGED - ACTION TAKEN</span>
        </div>
      )}
      {!showStamp && statusConfig.status === 'expiring_soon' && (
        <div className="bg-amber-600 text-center py-2">
          <span className="text-white text-xs font-bold">VISA EXPIRING SOON</span>
        </div>
      )}
      {!showStamp && statusConfig.status === 'expired' && (
        <div className="bg-red-600 text-center py-2">
          <span className="text-white text-xs font-bold">
            {daysRemaining < 0 ? 'VISA EXPIRED' : 'URGENT ACTION REQUIRED'}
          </span>
        </div>
      )}

      {/* Main Content */}
      <CardContent className={`p-4 relative ${showStamp ? 'opacity-80' : ''}`}>
        {/* Status Changed Stamp - Rectangular */}
        {showStamp && (
          <div className="mb-3 flex justify-center">
            <div className="border-4 border-emerald-500 rounded-lg px-6 py-2.5 bg-emerald-900/95 shadow-lg shadow-emerald-500/30">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-emerald-400" />
                <div className="flex flex-col items-start">
                  <span className="text-emerald-300 text-sm font-black tracking-wider">STATUS CHANGED</span>
                  <span className="text-emerald-300 text-xs font-bold tracking-wider">تم تغيير الحالة</span>
                  <span className="text-emerald-200 text-[10px] font-medium mt-0.5">
                    {visa.statusChangedAt || visa.actionTakenDate 
                      ? new Date(visa.statusChangedAt || visa.actionTakenDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() 
                      : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Person Info Row */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} flex items-center justify-center overflow-hidden flex-shrink-0`}>
            {visa.passenger_photo ? (
              <img src={formatBase64Image(visa.passenger_photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#F6C13B] font-bold text-sm">{initials}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white truncate">{visa.passenger_name || 'Unknown'}</h3>
            <div className="flex items-center gap-2 text-xs text-white/50">
              {visa.passport_number && (
                <span className="bg-white/10 px-2 py-0.5 rounded font-mono">{visa.passport_number}</span>
              )}
              {visa.nationality && <span>{visa.nationality}</span>}
            </div>
          </div>
          {visa.documentCount > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewDocs && onViewDocs(visa); }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-500/15 border border-orange-500/40 text-orange-300 hover:bg-orange-500/30 transition-colors flex-shrink-0 cursor-pointer"
              title={`عرض ${visa.documentCount} مستند مرفق`}
              data-testid={`visa-doc-count-${visa.id}`}
            >
              <Paperclip className="h-3.5 w-3.5" />
              <span className="text-xs font-bold">{visa.documentCount}</span>
            </button>
          )}
        </div>

        {/* Visa Details Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Expiry Date
            </div>
            <div className="text-sm font-medium text-white">{visa.expiry_date || 'N/A'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              Visa No.
            </div>
            <div className="text-sm font-medium text-white">{visa.visa_number || 'N/A'}</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1">Duration</div>
            <div className="text-sm font-medium text-white">{visa.duration_days || 'N/A'} days</div>
          </div>
          <div className="bg-[#0F172A]/50 rounded-lg p-3">
            <div className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Country
            </div>
            <div className="text-sm font-medium text-white">{visa.visa_country || 'UAE'}</div>
          </div>
        </div>

        {/* Birth Date & Passport Expiry Row */}
        <div className="grid grid-cols-2 gap-2 mb-3 bg-[#0F172A]/40 rounded-lg p-3">
          <div className="text-center">
            <div className="text-[10px] text-white/50 mb-1 flex items-center justify-center gap-1">
              <User className="h-3 w-3" />
              Date of Birth
            </div>
            <div className="text-sm text-white/80">{visa.date_of_birth || 'N/A'}</div>
          </div>
          <div className="text-center border-l border-white/10">
            <div className="text-[10px] text-white/50 mb-1">Passport Expiry</div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSetPassportExpiry && onSetPassportExpiry(visa.id);
              }}
              className={`text-sm ${visa.passport_expiry ? 'text-amber-400' : 'text-amber-500 underline decoration-dotted'} hover:text-amber-300 transition-colors cursor-pointer`}
              title="Click to edit / اضغط للتعديل"
              data-testid={`edit-passport-expiry-${visa.id}`}
            >
              {visa.passport_expiry || 'Click to set'}
            </button>
          </div>
        </div>

        {/* Days Counter */}
        {daysRemaining !== null && !isNaN(daysRemaining) && (
          <div className={`text-center py-3 rounded-xl ${statusConfig.bgColor} border ${statusConfig.borderColor} mb-3`}>
            <div className={`text-3xl font-bold ${statusConfig.color}`}>{daysText}</div>
            <div className="text-[10px] text-white/50">{daysLabel}</div>
          </div>
        )}

        {/* Linked Worker Section */}
        {visa.linkedCaseId && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] text-emerald-400 font-bold uppercase">
                  LINKED WORKER
                  {/* Show match confidence badge */}
                  {visa.matchConfidence && (
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[8px] ${
                      visa.matchConfidence === 'exact_passport' ? 'bg-emerald-500/30 text-emerald-300' :
                      visa.matchConfidence === 'manual' ? 'bg-blue-500/30 text-blue-300' :
                      'bg-amber-500/30 text-amber-300'
                    }`}>
                      {visa.matchConfidence === 'exact_passport' ? '✓ PASSPORT MATCH' :
                       visa.matchConfidence === 'manual' ? '✓ MANUAL' :
                       `⚠️ ${visa.matchConfidence.replace('name_match_', '').replace('name_nationality', 'NAME MATCH')}`}
                    </span>
                  )}
                </div>
                <div className="text-sm text-white font-medium">{visa.linkedWorkerName || 'Unknown'}</div>
              </div>
              <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                visa.linkedWorkerStatus === 'arrived' ? 'bg-emerald-500/20 text-emerald-400' :
                visa.linkedWorkerStatus === 'runaway' ? 'bg-red-500/20 text-red-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {getStatusIcon(visa.linkedWorkerStatus)} {(visa.linkedWorkerStatus || 'abroad').toUpperCase()}
              </span>
            </div>
            {visa.linkedWorkerProcessStatus && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-white/50">Process:</span>
                <span className={`px-2 py-0.5 rounded ${
                  visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' 
                    ? 'bg-amber-500/20 text-amber-400' 
                    : 'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' ? '🔄 CHANGE STATUS' : '✓ NORMAL'}
                </span>
              </div>
            )}
            {/* Action Buttons for Linked Worker */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-emerald-500/20">
              <Button 
                size="sm"
                onClick={() => onToggleProcess && onToggleProcess(
                  visa.linkedCaseId, 
                  visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' ? 'NORMAL' : 'CHANGE_STATUS'
                )}
                className={`flex-1 text-xs font-semibold h-8 ${
                  visa.linkedWorkerProcessStatus === 'CHANGE_STATUS'
                    ? 'bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:bg-blue-500/30'
                    : 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30'
                }`}
              >
                {visa.linkedWorkerProcessStatus === 'CHANGE_STATUS' ? 'RESET STATUS' : 'CHANGE STATUS'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onUnlink && onUnlink(visa.id)}
                className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10 border border-red-500/30"
                title="فك الربط / Unlink"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onLink && onLink(visa)}
                className="h-8 w-8 p-0 text-amber-400 hover:bg-amber-500/10 border border-amber-500/30"
                title="إعادة الربط / Re-link"
              >
                <Link className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Suggested Link Warning - for auto-link suggestions */}
        {!visa.linkedCaseId && visa.suggestedLinkCaseId && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <span className="text-amber-400 text-xs font-bold">اقتراح ربط / SUGGESTED LINK</span>
              <span className="text-amber-300 text-[10px] bg-amber-500/20 px-1.5 py-0.5 rounded">
                {visa.suggestedLinkScore}
              </span>
            </div>
            <div className="text-white text-sm mb-2">{visa.suggestedLinkWorkerName}</div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => onLink && onLink(visa, visa.suggestedLinkCaseId)}
                className="flex-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30 text-xs h-7"
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                قبول / Accept
              </Button>
              <Button
                size="sm"
                onClick={() => onLink && onLink(visa)}
                className="flex-1 bg-amber-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 text-xs h-7"
              >
                <Link className="h-3 w-3 mr-1" />
                اختيار آخر / Choose Other
              </Button>
            </div>
          </div>
        )}

        {/* Not Linked Warning */}
        {!visa.linkedCaseId && (
          <div className="bg-red-500/5 border border-dashed border-red-500/30 rounded-xl p-3 mb-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <span className="text-red-400 text-xs font-medium">Not linked to worker</span>
            </div>
            <Button
              size="sm"
              onClick={() => onLink && onLink(visa)}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-semibold h-7"
            >
              <Link className="h-3 w-3 mr-1" />
              Link Now
            </Button>
          </div>
        )}

        {/* Action Buttons - View & Delete */}
        <div className="flex gap-2">
          <Button
            onClick={() => onView(visa)}
            variant="outline"
            className="flex-1 h-9 border-white/20 text-white hover:bg-white/10"
          >
            <Eye className="h-4 w-4 mr-2" />
            View
          </Button>
          <Button
            onClick={() => onDelete(visa)}
            variant="outline"
            className="h-9 px-3 border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const VisaCenterPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cancellationInputRef = useRef(null);
  
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanType, setScanType] = useState('entry_permit'); // 'entry_permit' | 'cancellation'
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [showExtractedModal, setShowExtractedModal] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12; // Visas per page
  
  // Manual Add Visa Dialog State
  const [showAddVisaModal, setShowAddVisaModal] = useState(false);
  const [visaForm, setVisaForm] = useState({
    passenger_name: '',
    passenger_name_arabic: '',
    passport_number: '',
    visa_number: '',
    nationality: '',
    visa_type: 'Work',
    place_of_birth: '',
    date_of_birth: '',
    passport_expiry: '',
    profession: '',
    entry_date: new Date().toISOString().split('T')[0],
    duration_days: 60, // NEW: Default to 60 days
    passenger_photo: ''
  });
  const [photoPreview, setPhotoPreview] = useState('');
  const photoInputRef = useRef(null);
  
  // Link Dialog State
  const [linkDialog, setLinkDialog] = useState({ open: false, visa: null });
  const [docsDialog, setDocsDialog] = useState({ open: false, visa: null });
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerSearch, setWorkerSearch] = useState('');
  const [linking, setLinking] = useState(false);

  // Load visas
  const loadVisas = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/visas');
      setVisas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading visas:', error);
      toast.error('Failed to load visas');
    } finally {
      setLoading(false);
    }
  };

  // Load workers for linking
  const loadWorkers = async () => {
    try {
      const data = await apiGet('/api/cases');
      setWorkers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading workers:', error);
    }
  };

  // Stable refs so the effects below have correct deps without re-running on every render.
  const loadVisasRef = useRef(loadVisas);
  const loadWorkersRef = useRef(loadWorkers);
  useEffect(() => {
    loadVisasRef.current = loadVisas;
    loadWorkersRef.current = loadWorkers;
  });

  useEffect(() => {
    loadVisasRef.current();
    loadWorkersRef.current();
  }, []);

  // Auto-refresh every 1 hour
  useEffect(() => {
    const interval = setInterval(() => {
      loadVisasRef.current();
      loadWorkersRef.current();
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Filter visas
  const filteredVisas = useMemo(() => {
    return visas.filter(v => 
      (v.passenger_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (v.visa_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (v.nationality || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [visas, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredVisas.length / PAGE_SIZE);
  const paginatedVisas = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredVisas.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredVisas, currentPage, PAGE_SIZE]);

  // (pagination reset on search is handled inline in the search input onChange)


  // AI Scanner — supports both Entry Permit and Cancellation scan types
  const handleAIScan = async (event, type = 'entry_permit') => {
    const file = event.target.files?.[0];
    if (!file) return;

    setScanType(type);
    setScanning(true);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const result = await apiPost('/api/visas/scan', {
        file_base64: base64,
        file_mime_type: file.type || 'image/jpeg',
        scan_type: type
      });

      if (result?.success && result.visa_id) {
        toast.success('Visa added successfully!');
        loadVisas();
      } else if (result?.success && result.extracted_data) {
        setExtractedData(result.extracted_data);
        setShowExtractedModal(true);
        if (type === 'cancellation' && result.extracted_data.cancel_date) {
          toast.success(`Cancellation scanned. Expiry auto-set to ${result.extracted_data.expiry_date} (Cancel Date + 30 days). Please review.`);
        } else {
          toast.success('Data extracted. Please review and save.');
        }
      } else {
        throw new Error('Failed to process visa');
      }
    } catch (error) {
      console.error('Scan error:', error);
      toast.error('Failed to scan visa: ' + (error.message || 'Unknown error'));
    } finally {
      setScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cancellationInputRef.current) cancellationInputRef.current.value = '';
    }
  };

  // Delete visa
  const handleDelete = async (visa) => {
    if (!window.confirm(`هل أنت متأكد من حذف تأشيرة ${visa.passenger_name}؟ سيتم حذف المستندات المرفقة نهائياً.`)) {
      return;
    }

    try {
      await apiDelete(`/api/visas/${visa.id}`);
      toast.success('Visa deleted');
      loadVisas();
    } catch (error) {
      toast.error('Failed to delete visa');
    }
  };

  // Toggle process status
  const handleToggleProcess = async (caseId, newStatus) => {
    try {
      await apiPost(`/api/cases/${caseId}/process-status`, { processStatus: newStatus });
      toast.success(newStatus === 'CHANGE_STATUS' ? '🔄 Change Status marked' : '✅ Status set to Normal');
      loadVisas();
    } catch (error) {
      toast.error('Failed to update process status');
    }
  };

  // Unlink visa from worker
  const handleUnlink = async (visaId) => {
    if (!window.confirm('Are you sure you want to unlink this visa from the worker?')) {
      return;
    }
    
    try {
      await apiPost(`/api/visas/${visaId}/unlink`);
      toast.success('Visa unlinked');
      loadVisas();
    } catch (error) {
      toast.error('Failed to unlink visa');
    }
  };

  // Link visa to worker
  const handleLinkVisa = async () => {
    if (!selectedWorker || !linkDialog.visa) return;
    
    setLinking(true);
    try {
      await apiPost('/api/visas/link', {
        visa_id: linkDialog.visa.id,
        case_id: selectedWorker.id
      });
      toast.success('تم ربط الفيزا بالعامل بنجاح! / Visa linked successfully!');
      setLinkDialog({ open: false, visa: null });
      setSelectedWorker(null);
      setWorkerSearch('');
      loadVisas();
    } catch (error) {
      console.error('Link error:', error);
      toast.error('فشل في ربط الفيزا / Failed to link visa');
    } finally {
      setLinking(false);
    }
  };

  // Handle photo upload for manual visa entry
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      setPhotoPreview(base64);
      setVisaForm(prev => ({ ...prev, passenger_photo: base64 }));
    };
    reader.readAsDataURL(file);
  };

  // Save manual visa entry
  const handleSaveManualVisa = async () => {
    if (!visaForm.passenger_name) {
      toast.error('اسم المسافر مطلوب / Passenger name is required');
      return;
    }
    if (!visaForm.passport_number) {
      toast.error('رقم الجواز مطلوب / Passport number is required');
      return;
    }
    if (!visaForm.passport_expiry) {
      toast.error('تاريخ انتهاء الجواز مطلوب / Passport expiry date is required');
      return;
    }
    if (!visaForm.date_of_birth) {
      toast.error('تاريخ الميلاد مطلوب / Date of birth is required');
      return;
    }
    
    setSaving(true);
    try {
      // Format date to DD/MM/YYYY as expected by backend
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      
      // Calculate expiry date based on selected duration_days
      const durationDays = visaForm.duration_days || 60;
      const entryDate = new Date(visaForm.entry_date || new Date());
      const expiryDate = new Date(entryDate);
      expiryDate.setDate(expiryDate.getDate() + durationDays);
      
      const visaData = {
        passenger_name: visaForm.passenger_name,
        passenger_name_arabic: visaForm.passenger_name_arabic || '',
        passport_number: visaForm.passport_number,
        visa_number: visaForm.visa_number || `MANUAL-${Date.now()}`,
        nationality: visaForm.nationality || 'Unknown',
        visa_type: visaForm.visa_type || 'Work',
        visa_country: 'UAE',
        duration_days: durationDays,
        place_of_birth: visaForm.place_of_birth || '',
        date_of_birth: formatDate(visaForm.date_of_birth),
        passport_expiry: formatDate(visaForm.passport_expiry),
        profession: visaForm.profession || '',
        entry_date: formatDate(visaForm.entry_date),
        issue_date: formatDate(visaForm.entry_date), // Also set issue_date
        expiry_date: formatDate(expiryDate.toISOString().split('T')[0]),
        passenger_photo: visaForm.passenger_photo || '',
        status: 'active'
      };
      
      await apiPost('/api/visas', visaData);
      toast.success('تم إضافة الفيزا بنجاح! / Visa added successfully!');
      setShowAddVisaModal(false);
      setVisaForm({
        passenger_name: '',
        passenger_name_arabic: '',
        passport_number: '',
        visa_number: '',
        nationality: '',
        visa_type: 'Work',
        place_of_birth: '',
        date_of_birth: '',
        passport_expiry: '',
        profession: '',
        entry_date: new Date().toISOString().split('T')[0],
        passenger_photo: ''
      });
      setPhotoPreview('');
      loadVisas();
    } catch (error) {
      console.error('Save error:', error);
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to save visa');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Save extracted visa data
  const handleSaveExtracted = async () => {
    if (!extractedData) return;
    
    // Validate required fields
    if (!extractedData.passenger_name) {
      toast.error('Passenger name is required');
      return;
    }
    if (!extractedData.expiry_date) {
      toast.error('Expiry date is required');
      return;
    }
    
    setSaving(true);
    try {
      // Prepare data with required fields matching Visa Pydantic model
      const visaData = {
        passenger_name: extractedData.passenger_name || '',
        passenger_name_arabic: extractedData.passenger_name_arabic || '',
        passport_number: extractedData.passport_number || '',
        nationality: extractedData.nationality || '',
        visa_country: extractedData.visa_country || 'UAE',
        visa_number: extractedData.visa_number || '',
        visa_type: extractedData.visa_type || 'Work Visa',
        uid_number: extractedData.uid_number || '',
        place_of_birth: extractedData.place_of_birth || '',
        date_of_birth: extractedData.date_of_birth || '',
        passport_expiry: extractedData.passport_expiry || '',
        profession: extractedData.profession || '',
        duration_days: parseInt(extractedData.duration_days) || 60,
        issue_date: extractedData.issue_date || null,
        entry_date: extractedData.entry_date || null,
        expiry_date: extractedData.expiry_date || '',
        sponsor_name: extractedData.sponsor_name || '',
        entry_place: extractedData.entry_place || '',
        passenger_photo: extractedData.passenger_photo || '',
        status: 'active',
        contact_email: '',
        contact_whatsapp: '',
        notes: ''
      };
      
      console.log('Saving visa data:', visaData);
      
      const result = await apiPost('/api/visas', visaData);
      console.log('Save result:', result);
      
      if (result?.id || result?.message) {
        toast.success('Visa saved successfully!');
        setShowExtractedModal(false);
        setExtractedData(null);
        loadVisas();
      } else {
        throw new Error('Failed to save visa');
      }
    } catch (error) {
      console.error('Save error:', error);
      // Handle error message properly - error.message contains the detail from backend
      const errorMsg = typeof error.message === 'string' ? error.message : 'Unknown error';
      toast.error('Failed to save visa: ' + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Update extracted data field
  const updateExtractedField = (field, value) => {
    setExtractedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Stats calculation with new logic - uses entry_date + duration_days
  const stats = {
    total: visas.length,
    active: visas.filter(v => {
      const days = calculateDaysRemaining(v.expiry_date, v.entry_date, v.duration_days, v.issue_date);
      return days === null || days > 10;
    }).length,
    expiringSoon: visas.filter(v => {
      const days = calculateDaysRemaining(v.expiry_date, v.entry_date, v.duration_days, v.issue_date);
      return days !== null && days > 5 && days <= 10;
    }).length,
    expired: visas.filter(v => {
      const days = calculateDaysRemaining(v.expiry_date, v.entry_date, v.duration_days, v.issue_date);
      return days !== null && days <= 5;
    }).length
  };

  // Inline-edit Passport Expiry on an existing visa card
  const handleSetEntryDate = async (visaId) => {
    const visa = visas.find((v) => v.id === visaId);
    if (!visa) return;
    const currentValue = visa.entry_date || '';
    const input = window.prompt(
      'أدخل تاريخ الدخول الفعلي / Enter Actual Entry Date (DD/MM/YYYY or YYYY-MM-DD)',
      currentValue
    );
    if (input === null) return;
    const trimmed = (input || '').trim();
    if (!trimmed || !/^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/.test(trimmed)) {
      toast.error('Invalid format. Use DD/MM/YYYY or YYYY-MM-DD.');
      return;
    }
    // Normalize to a Date, then recompute expiry = entry + duration_days
    let d, m, y;
    if (trimmed.includes('/')) {
      [d, m, y] = trimmed.split('/').map(Number);
    } else {
      [y, m, d] = trimmed.split('-').map(Number);
    }
    const entryObj = new Date(y, m - 1, d);
    // Reject impossible calendar dates — JS silently normalizes (e.g. 31/02 → 03/03).
    if (
      isNaN(entryObj.getTime()) ||
      entryObj.getFullYear() !== y ||
      entryObj.getMonth() !== m - 1 ||
      entryObj.getDate() !== d
    ) {
      toast.error('تاريخ غير صالح / Invalid date');
      return;
    }
    const duration = parseInt(visa.duration_days) || 30;
    const expiryObj = new Date(entryObj);
    expiryObj.setDate(expiryObj.getDate() + duration);
    const fmt = (dt) => `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
    const entry_date = fmt(entryObj);
    const expiry_date = fmt(expiryObj);
    try {
      await apiPut(`/api/visas/${visaId}`, { ...visa, entry_date, expiry_date });
      toast.success(`تم تحديث تاريخ الدخول — تنتهي ${expiry_date}`);
      setSelectedVisa((prev) => (prev && prev.id === visaId ? { ...prev, entry_date, expiry_date } : prev));
      loadVisas();
    } catch (e) {
      console.error(e);
      toast.error('Failed to update entry date');
    }
  };

  const handleSetPassportExpiry = async (visaId) => {
    const visa = visas.find((v) => v.id === visaId);
    if (!visa) return;
    const currentValue = visa.passport_expiry || '';
    const input = window.prompt(
      'أدخل تاريخ انتهاء الجواز / Enter Passport Expiry (DD/MM/YYYY or YYYY-MM-DD)',
      currentValue
    );
    if (input === null) return;
    const trimmed = (input || '').trim();
    if (trimmed && !/^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/.test(trimmed)) {
      toast.error('Invalid format. Use DD/MM/YYYY or YYYY-MM-DD.');
      return;
    }
    try {
      // PUT requires the full Visa object — spread the existing visa and override passport_expiry
      await apiPut(`/api/visas/${visaId}`, { ...visa, passport_expiry: trimmed });
      toast.success('Passport expiry updated');
      loadVisas();
    } catch (e) {
      console.error(e);
      toast.error('Failed to update passport expiry');
    }
  };

  return (
    <div className="space-y-6" data-testid="visa-center-page">
      {/* Header with gradient */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="h-11 w-11 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50"
        >
          <ArrowLeft className="h-5 w-5 text-white" />
        </Button>
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-white">Visas</h1>
          <p className="text-slate-400 text-sm">مركز إدارة التأشيرات</p>
        </div>
      </motion.div>

      {/* Action Buttons - AI Scanner prominent */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex gap-3"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => handleAIScan(e, 'entry_permit')}
          className="hidden"
        />
        <input
          ref={cancellationInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => handleAIScan(e, 'cancellation')}
          className="hidden"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={scanning}
          className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 h-14 rounded-xl font-black text-lg shadow-lg shadow-orange-500/30 border-0"
          data-testid="ai-scanner-btn"
        >
          {scanning && scanType === 'entry_permit' ? (
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
          ) : (
            <Scan className="h-6 w-6 mr-2" />
          )}
          AI · ENTRY VISA
        </Button>
        <Button
          onClick={() => cancellationInputRef.current?.click()}
          disabled={scanning}
          className="flex-1 bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 h-14 rounded-xl font-black text-lg shadow-lg shadow-rose-600/30 border-0"
          data-testid="ai-scanner-cancellation-btn"
        >
          {scanning && scanType === 'cancellation' ? (
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
          ) : (
            <Scan className="h-6 w-6 mr-2" />
          )}
          AI · CANCELLATION
        </Button>
        <Button
          onClick={() => setShowAddVisaModal(true)}
          variant="outline"
          className="h-14 px-6 rounded-xl font-bold border-slate-700/50 bg-slate-800/50 hover:bg-slate-700/50 text-white"
        >
          <Plus className="h-5 w-5 mr-2" />
          ADD VISA
        </Button>
      </motion.div>

      {/* Search Bar */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="relative"
      >
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
        <Input
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (currentPage !== 1) setCurrentPage(1);
          }}
          placeholder="Search by name or visa number..."
          className="pl-12 h-12 bg-slate-900/80 border-slate-700/50 text-white rounded-xl placeholder:text-slate-500"
          data-testid="visa-search-input"
        />
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-4 gap-3"
      >
        <div className="text-center p-4 bg-slate-900/80 backdrop-blur-sm rounded-xl border border-slate-700/50">
          <p className="text-2xl font-black text-white">{stats.total}</p>
          <p className="text-xs text-slate-400 font-medium">Total</p>
        </div>
        <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
          <p className="text-2xl font-black text-emerald-400">{stats.active}</p>
          <p className="text-xs text-emerald-400/70 font-medium">Active (&gt;10d)</p>
        </div>
        <div className="text-center p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <p className="text-2xl font-black text-amber-400">{stats.expiringSoon}</p>
          <p className="text-xs text-amber-400/70 font-medium">Soon (6-10d)</p>
        </div>
        <div className="text-center p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-2xl font-black text-red-400">{stats.expired}</p>
          <p className="text-xs text-red-400/70 font-medium">Urgent (≤5d)</p>
        </div>
      </motion.div>

      {/* Visa List */}
      <div className="space-y-4">
        {loading ? (
          <VisaCenterLoading />
        ) : filteredVisas.length === 0 ? (
          <Card className="bg-slate-900/80 border-slate-700/50 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="h-20 w-20 rounded-full bg-slate-800/80 flex items-center justify-center mx-auto mb-4">
                <Scan className="h-10 w-10 text-slate-500" />
              </div>
              <p className="text-slate-400 text-lg font-medium mb-2">
                {searchTerm ? 'No search results' : 'No visas found'}
              </p>
              <p className="text-slate-500 text-sm">
                Use AI Scanner to add a new visa
              </p>
            </CardContent>
          </Card>
        ) : (
          paginatedVisas.map(visa => (
            <VisaCard
              key={visa.id}
              visa={visa}
              onView={(v) => setSelectedVisa(v)}
              onDelete={handleDelete}
              onToggleProcess={handleToggleProcess}
              onUnlink={handleUnlink}
              onLink={(v) => setLinkDialog({ open: true, visa: v })}
              onSetPassportExpiry={handleSetPassportExpiry}
              onViewDocs={(v) => setDocsDialog({ open: true, visa: v })}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredVisas.length > PAGE_SIZE && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredVisas.length}
            pageSize={PAGE_SIZE}
            isLoading={loading}
          />
        </div>
      )}

      {/* View Visa Dialog */}
      <Dialog open={!!selectedVisa} onOpenChange={() => { setSelectedVisa(null); loadVisas(); }}>
        <DialogContent className="bg-[#1A2D47] border-[#004B93]/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Visa Details</DialogTitle>
          </DialogHeader>
          {selectedVisa && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl bg-[#0F2540] flex items-center justify-center overflow-hidden">
                  {selectedVisa.passenger_photo ? (
                    <img src={formatBase64Image(selectedVisa.passenger_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="h-10 w-10 text-white/30" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-lg">{selectedVisa.passenger_name}</h3>
                  <p className="text-sm text-white/60">{selectedVisa.nationality}</p>
                  <p className="text-xs text-white/40 font-mono">#{selectedVisa.visa_number || selectedVisa.id}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-[#0F2540] rounded-lg" data-testid="visa-entry-date-cell">
                  <p className="text-white/50 text-xs">Entry Date / تاريخ الدخول</p>
                  <button
                    type="button"
                    onClick={() => handleSetEntryDate(selectedVisa.id)}
                    className={`font-medium text-left ${selectedVisa.entry_date ? 'text-cyan-300' : 'text-cyan-500 underline decoration-dotted'} hover:text-cyan-200 transition-colors cursor-pointer`}
                    title="اضغط لتصحيح تاريخ الدخول الفعلي"
                    data-testid="visa-entry-date-edit"
                  >
                    {selectedVisa.entry_date || 'Click to set'}
                  </button>
                </div>
                <div className="p-3 bg-[#0F2540] rounded-lg">
                  <p className="text-white/50 text-xs">Issue Date</p>
                  <p className="font-medium">{selectedVisa.issue_date || '-'}</p>
                </div>
                <div className="p-3 bg-[#0F2540] rounded-lg">
                  <p className="text-white/50 text-xs">Expiry Date</p>
                  <p className="font-medium">{selectedVisa.expiry_date || '-'}</p>
                </div>
                <div className="p-3 bg-[#0F2540] rounded-lg">
                  <p className="text-white/50 text-xs">Duration</p>
                  <p className="font-medium">{selectedVisa.duration_days || 60} days</p>
                </div>
                <div className="p-3 bg-[#0F2540] rounded-lg">
                  <p className="text-white/50 text-xs">Type</p>
                  <p className="font-medium">{selectedVisa.visa_type || 'Work Visa'}</p>
                </div>
                <div className="p-3 bg-[#0F2540] rounded-lg">
                  <p className="text-white/50 text-xs">Date of Birth</p>
                  <p className="font-medium">{selectedVisa.date_of_birth || '-'}</p>
                </div>
                <div className="p-3 bg-[#0F2540] rounded-lg">
                  <p className="text-white/50 text-xs">Passport Expiry</p>
                  <p className="font-medium text-amber-400">{selectedVisa.passport_expiry || '-'}</p>
                </div>
              </div>

              {selectedVisa.entry_place && (
                <div className="p-3 bg-[#0F2540] rounded-lg text-sm">
                  <p className="text-white/50 text-xs">Entry Point</p>
                  <p className="font-medium">{selectedVisa.entry_place}</p>
                </div>
              )}

              {/* Attached worker documents (purged when this visa is deleted) */}
              {selectedVisa.linkedCaseId && (
                <div className="p-3 bg-[#0F2540] rounded-lg" data-testid="visa-documents-section">
                  <p className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-orange-400" />
                    المستندات / Documents
                  </p>
                  <WorkerDocuments caseId={selectedVisa.linkedCaseId} compact />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attachments Quick-View Dialog (opened from the 📎 badge on a visa card) */}
      <Dialog
        open={docsDialog.open}
        onOpenChange={(open) => { if (!open) { setDocsDialog({ open: false, visa: null }); loadVisas(); } }}
      >
        <DialogContent className="bg-[#1A2D47] border-[#004B93]/30 text-white max-w-md" data-testid="visa-docs-quick-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-400" />
              المستندات المرفقة / Attachments
            </DialogTitle>
          </DialogHeader>
          {docsDialog.visa && (
            <div className="space-y-3">
              <p className="text-sm text-white/60">{docsDialog.visa.passenger_name}</p>
              <WorkerDocuments caseId={docsDialog.visa.linkedCaseId} compact />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showExtractedModal} onOpenChange={setShowExtractedModal}>
        <DialogContent className="bg-[#1A2D47] border-[#004B93]/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-400" />
              Review Extracted Data
            </DialogTitle>
          </DialogHeader>

          {extractedData && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-emerald-400 bg-emerald-500/10 p-3 rounded-lg">
                ✓ Data extracted successfully! Please review and edit if needed before saving.
              </p>

              {/* Photo Upload Section */}
              <div className="bg-[#0F2540] rounded-xl p-4 border border-[#004B93]/30">
                <Label className="text-white/70 mb-3 block">صورة العاملة / Passenger Photo</Label>
                <div className="flex items-center gap-4">
                  {/* Photo Preview */}
                  <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 border-2 border-slate-600 bg-slate-800">
                    {extractedData.passenger_photo ? (
                      <img 
                        src={formatBase64Image(extractedData.passenger_photo)} 
                        alt="Passenger" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                        <User className="h-8 w-8 mb-1" />
                        <span className="text-[10px]">No Photo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      id="photo-upload"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = () => {
                            updateExtractedField('passenger_photo', reader.result);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <label
                      htmlFor="photo-upload"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/40 rounded-lg cursor-pointer hover:bg-blue-500/30 transition-colors"
                    >
                      <Upload className="h-4 w-4 text-blue-400" />
                      <span className="text-blue-400 text-sm font-medium">رفع صورة / Upload Photo</span>
                    </label>
                    <p className="text-xs text-slate-500 mt-2">JPG, PNG - Max 5MB</p>
                    {extractedData.passenger_photo && (
                      <button
                        onClick={() => updateExtractedField('passenger_photo', '')}
                        className="text-xs text-red-400 mt-1 hover:text-red-300"
                      >
                        ✕ إزالة الصورة / Remove Photo
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Passenger Name</Label>
                  <Input
                    value={extractedData.passenger_name || ''}
                    onChange={(e) => updateExtractedField('passenger_name', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Nationality</Label>
                  <Input
                    value={extractedData.nationality || ''}
                    onChange={(e) => updateExtractedField('nationality', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Visa Number</Label>
                  <Input
                    value={extractedData.visa_number || ''}
                    onChange={(e) => updateExtractedField('visa_number', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Passport Number</Label>
                  <Input
                    value={extractedData.passport_number || ''}
                    onChange={(e) => updateExtractedField('passport_number', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Issue Date</Label>
                  <Input
                    type="date"
                    value={extractedData.issue_date || ''}
                    onChange={(e) => updateExtractedField('issue_date', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">🚪 Entry Date / تاريخ الدخول</Label>
                  <Input
                    type="date"
                    value={extractedData.entry_date || ''}
                    onChange={(e) => updateExtractedField('entry_date', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Expiry Date</Label>
                  <Input
                    type="date"
                    value={extractedData.expiry_date || ''}
                    onChange={(e) => updateExtractedField('expiry_date', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">UID Number</Label>
                  <Input
                    value={extractedData.uid_number || ''}
                    onChange={(e) => updateExtractedField('uid_number', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Visa Type</Label>
                  <Input
                    value={extractedData.visa_type || ''}
                    onChange={(e) => updateExtractedField('visa_type', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Sponsor Name</Label>
                  <Input
                    value={extractedData.sponsor_name || ''}
                    onChange={(e) => updateExtractedField('sponsor_name', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Entry Place</Label>
                  <Input
                    value={extractedData.entry_place || ''}
                    onChange={(e) => updateExtractedField('entry_place', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70 flex items-center gap-2">
                    <span className="text-red-500">*</span>
                    مدة الفيزا / Visa Duration
                  </Label>
                  <select
                    value={extractedData.duration_days || 60}
                    onChange={(e) => updateExtractedField('duration_days', parseInt(e.target.value))}
                    className="w-full h-10 bg-[#0F2540] border-2 border-orange-500/50 text-white rounded-md px-3"
                  >
                    <option value={30}>30 يوم / 30 Days</option>
                    <option value={60}>60 يوم / 60 Days</option>
                    <option value={90}>90 يوم / 90 Days</option>
                  </select>
                  <p className="text-orange-400 text-[10px]">⚠️ هام: هذا يحدد متى سيتم تغيير الحالة</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Profession</Label>
                  <Input
                    value={extractedData.profession || ''}
                    onChange={(e) => updateExtractedField('profession', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Date of Birth</Label>
                  <Input
                    type="date"
                    value={extractedData.date_of_birth || ''}
                    onChange={(e) => updateExtractedField('date_of_birth', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Passport Expiry Date</Label>
                  <Input
                    type="date"
                    value={extractedData.passport_expiry || ''}
                    onChange={(e) => updateExtractedField('passport_expiry', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Visa Country</Label>
                  <Input
                    value={extractedData.visa_country || 'UAE'}
                    onChange={(e) => updateExtractedField('visa_country', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Place of Birth</Label>
                  <Input
                    value={extractedData.place_of_birth || ''}
                    onChange={(e) => updateExtractedField('place_of_birth', e.target.value)}
                    className="bg-[#0F2540] border-[#004B93]/30 text-white"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowExtractedModal(false);
                setExtractedData(null);
              }}
              className="border-white/20 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveExtracted}
              disabled={saving}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Visa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Visa to Worker Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => {
        setLinkDialog({ open, visa: open ? linkDialog.visa : null });
        if (!open) {
          setSelectedWorker(null);
          setWorkerSearch('');
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link className="h-5 w-5 text-emerald-500" />
              ربط الفيزا بعامل / Link Visa to Worker
            </DialogTitle>
          </DialogHeader>
          
          {linkDialog.visa && (
            <div className="bg-slate-800/50 rounded-xl p-3 mb-4 border border-slate-700">
              <p className="text-slate-400 text-xs">Selected Visa:</p>
              <p className="text-white font-medium">{linkDialog.visa.passenger_name}</p>
              <p className="text-slate-500 text-xs font-mono">#{linkDialog.visa.visa_number || linkDialog.visa.id}</p>
            </div>
          )}
          
          <div className="space-y-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                value={workerSearch}
                onChange={(e) => setWorkerSearch(e.target.value)}
                placeholder="البحث عن عامل... / Search workers..."
                className="pl-10 bg-slate-800 border-slate-700 text-white rounded-xl"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {workers
                .filter(w => !w.visaId) // Only show workers without visa
                .filter(w => (w.workerName || '').toLowerCase().includes(workerSearch.toLowerCase()))
                .map(worker => (
                  <div
                    key={worker.id}
                    onClick={() => setSelectedWorker(worker)}
                    className={`p-3 rounded-xl cursor-pointer transition-all ${
                      selectedWorker?.id === worker.id
                        ? 'bg-emerald-500/20 border border-emerald-500/50'
                        : 'bg-slate-800/50 border border-transparent hover:border-slate-700'
                    }`}
                  >
                    <p className="text-white font-medium">{worker.workerName}</p>
                    <p className="text-slate-500 text-sm">{worker.nationality} • {worker.passportNumber || 'No passport'}</p>
                  </div>
                ))}
              {workers.filter(w => !w.visaId).length === 0 && (
                <p className="text-slate-500 text-center py-4 text-sm">
                  لا يوجد عمال متاحين للربط / No workers available for linking
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => {
                setLinkDialog({ open: false, visa: null });
                setSelectedWorker(null);
                setWorkerSearch('');
              }}
              className="text-slate-400"
            >
              إلغاء / Cancel
            </Button>
            <Button
              onClick={handleLinkVisa}
              disabled={!selectedWorker || linking}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {linking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Link className="h-4 w-4 mr-2" />
                  ربط / Link
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Add Visa Dialog */}
      <Dialog open={showAddVisaModal} onOpenChange={setShowAddVisaModal}>
        <DialogContent className="bg-[#1A2D47] border-[#004B93]/30 text-white max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5 text-orange-500" />
              إضافة فيزا يدوياً / Add Visa Manually
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
            {/* Photo Upload */}
            <div className="flex flex-col items-center gap-3">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <div 
                onClick={() => photoInputRef.current?.click()}
                className="w-28 h-28 rounded-full border-2 border-dashed border-[#004B93]/50 bg-slate-800/50 flex items-center justify-center cursor-pointer hover:border-orange-500/50 transition-colors overflow-hidden"
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Camera className="h-8 w-8 text-slate-500 mx-auto mb-1" />
                    <span className="text-xs text-slate-500">صورة المسافر</span>
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => photoInputRef.current?.click()}
                className="text-orange-400 text-xs"
              >
                📷 رفع صورة / Upload Photo
              </Button>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
              {/* Passenger Name - English */}
              <div className="col-span-2">
                <label className="text-slate-400 text-xs mb-1 block">اسم المسافر (إنجليزي) / Passenger Name *</label>
                <Input
                  value={visaForm.passenger_name}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, passenger_name: e.target.value }))}
                  placeholder="Enter passenger name in English"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              {/* Passenger Name - Arabic */}
              <div className="col-span-2">
                <label className="text-slate-400 text-xs mb-1 block">اسم المسافر (عربي) / Passenger Name Arabic</label>
                <Input
                  value={visaForm.passenger_name_arabic}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, passenger_name_arabic: e.target.value }))}
                  placeholder="أدخل اسم المسافر بالعربي"
                  className="bg-slate-800 border-slate-700 text-white"
                  dir="rtl"
                />
              </div>
              
              {/* Passport Number */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">رقم الجواز / Passport Number *</label>
                <Input
                  value={visaForm.passport_number}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, passport_number: e.target.value }))}
                  placeholder="e.g. A12345678"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              {/* Visa Number */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">رقم الفيزا / Visa Number</label>
                <Input
                  value={visaForm.visa_number}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, visa_number: e.target.value }))}
                  placeholder="e.g. VISA-123456"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              {/* Nationality */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">الجنسية / Nationality</label>
                <Input
                  value={visaForm.nationality}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, nationality: e.target.value }))}
                  placeholder="e.g. Philippine"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              {/* Place of Birth */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">مكان الميلاد / Place of Birth</label>
                <Input
                  value={visaForm.place_of_birth}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, place_of_birth: e.target.value }))}
                  placeholder="e.g. Manila"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              {/* Date of Birth */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">تاريخ الميلاد / Date of Birth *</label>
                <Input
                  type="date"
                  value={visaForm.date_of_birth}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              {/* Passport Expiry */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">انتهاء الجواز / Passport Expiry *</label>
                <Input
                  type="date"
                  value={visaForm.passport_expiry}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, passport_expiry: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>
              
              {/* Profession */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">المهنة / Profession</label>
                <Input
                  value={visaForm.profession}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, profession: e.target.value }))}
                  placeholder="e.g. Domestic Worker"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              {/* Visa Type */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">نوع الفيزا / Visa Type</label>
                <select
                  value={visaForm.visa_type}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, visa_type: e.target.value }))}
                  className="w-full h-10 bg-slate-800 border border-slate-700 text-white rounded-md px-3"
                >
                  <option value="Work">Work Visa</option>
                  <option value="Visit">Visit Visa</option>
                  <option value="Tourist">Tourist Visa</option>
                  <option value="Residence">Residence Visa</option>
                </select>
              </div>
              
              {/* VISA DURATION - NEW CRITICAL FIELD */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block flex items-center gap-2">
                  <span className="text-red-500">*</span>
                  مدة الفيزا / Visa Duration
                </label>
                <select
                  value={visaForm.duration_days}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, duration_days: parseInt(e.target.value) }))}
                  className="w-full h-10 bg-slate-800 border-2 border-orange-500/50 text-white rounded-md px-3 focus:border-orange-500"
                  required
                >
                  <option value={30}>30 يوم / 30 Days</option>
                  <option value={60}>60 يوم / 60 Days</option>
                  <option value={90}>90 يوم / 90 Days</option>
                </select>
                <p className="text-orange-400 text-[10px] mt-1">⚠️ هام: هذا يحدد متى سيتم تغيير الحالة</p>
              </div>
              
              {/* Entry Date */}
              <div>
                <label className="text-slate-400 text-xs mb-1 block">تاريخ الدخول / Entry Date</label>
                <Input
                  type="date"
                  value={visaForm.entry_date}
                  onChange={(e) => setVisaForm(prev => ({ ...prev, entry_date: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowAddVisaModal(false);
                setVisaForm({
                  passenger_name: '',
                  passenger_name_arabic: '',
                  passport_number: '',
                  visa_number: '',
                  nationality: '',
                  visa_type: 'Work',
                  place_of_birth: '',
                  date_of_birth: '',
                  passport_expiry: '',
                  profession: '',
                  entry_date: new Date().toISOString().split('T')[0],
                  duration_days: 60,
                  passenger_photo: ''
                });
                setPhotoPreview('');
              }}
              className="text-slate-400"
            >
              إلغاء / Cancel
            </Button>
            <Button
              onClick={handleSaveManualVisa}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  حفظ الفيزا / Save Visa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisaCenterPage;
